import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import { Flex, Modal } from 'components';
import { useCreateUserMutation } from 'hooks/queries/useUsersQuery';
import { useLanguagesQuery } from 'hooks/queries/useLanguagesQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { ImageInput } from 'components/ImageInput/ImageInput';
import { IGenericModal } from 'components/Modal/Modal';
import { P } from 'components/Typography/Typography';
import type { IOption as INestedSelectOption } from 'components/_form/NestedSelect/NestedSelect';
import { collapseSelectedAreaPermissionIds } from 'components/_form/NestedSelect/parseNestedSelectSelectedLabels';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import { NestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import { Radio, Select, TextField } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import type { ICompany } from 'types/company';
import { IUserForm } from 'types/forms/user';
import { IUserFunction } from 'types/functions';
import { SUPPORTED_LANGUAGES } from 'types/language';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { companySupportedLanguageCodes } from 'utilities/companyLanguageCodes';
import { displayTranslatedField } from 'utilities/localizedString';

const Row = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 10px;
`;

const RadioGroup = styled(Flex)`
  gap: 30px;
  flex-wrap: wrap;
`;

const initialFormState = {
  company_id: '',
  id_code: '',
  first_name: '',
  second_name: '',
  last_name: '',
  email: '',
  phone: '',
  landline_phone: '',
  password: '',
  password_confirmation: '',
  language: '',
  role: 'user',
  role_in_company: [] as string[],
  area_ids: [] as string[],
  photo: null as File | null
};

/** Domyślny kod języka z listy firmy (zgodny z wartościami w Select). */
function defaultLanguageCodeForCompany(company: ICompany | null | undefined): string {
  if (!company) return '';
  const codes = companySupportedLanguageCodes(company);
  const preferred = (company.default_language_code ?? '').trim();
  if (!preferred) return codes[0] ?? '';
  const match = codes.find(
    (c) => c.trim().toLowerCase() === preferred.toLowerCase()
  );
  return match ?? codes[0] ?? '';
}

function defaultFormStateForCompany(company: ICompany | null | undefined) {
  return {
    ...initialFormState,
    company_id: company?.id ? String(company.id) : '',
    language: defaultLanguageCodeForCompany(company)
  };
}

export const UserAddModal = ({
  isOpen,
  onCancelClick
}: IGenericModal) => {
  const { t } = useTranslation();
  const createUserMutation = useCreateUserMutation();
  const { data: mainCompany } = useMainCompanyQuery();
  const { data: languagesFromApi } = useLanguagesQuery();
  const [formState, setFormState] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormState(defaultFormStateForCompany(mainCompany ?? undefined));
  }, [
    isOpen,
    mainCompany?.id,
    mainCompany?.default_language_code,
    mainCompany?.available_language_codes?.join(',')
  ]);

  const { data: areasData = [] } = useAreasQuery();
  const mainAreaOptions = React.useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();
  const roleInCompanyOptions = React.useMemo<IOption[]>(
    () =>
      userFunctionsData.map((fn: IUserFunction) => ({
        label: displayTranslatedField(fn, 'name'),
        value: String(fn.id)
      })),
    [userFunctionsData]
  );

  const languageOptions = React.useMemo<IOption[]>(() => {
    const base = (languagesFromApi?.length ? languagesFromApi : SUPPORTED_LANGUAGES).map((l) => ({
      value: l.code,
      label: String(
        t(`translationsView.tableHeaders.${String(l.code).trim().toLowerCase()}`, {
          defaultValue: l.name || l.code
        })
      )
    }));
    const supported = companySupportedLanguageCodes(mainCompany ?? undefined);
    if (!supported.length) {
      return base;
    }
    const supportedSet = new Set(supported.map((c) => String(c).trim().toLowerCase()));
    return base.filter((o) =>
      supportedSet.has(String(o.value).trim().toLowerCase())
    );
  }, [
    languagesFromApi,
    mainCompany?.available_language_codes,
    mainCompany?.default_language_code,
    t
  ]);

  const mainAreaSelectedOptions = React.useMemo(
    () =>
      formState.area_ids
        .map((vid) => findOptionByValueInTree(mainAreaOptions, vid))
        .filter((o): o is INestedSelectOption => !!o),
    [formState.area_ids, mainAreaOptions]
  );

  const companyOptions = React.useMemo(() => {
    if (!mainCompany) return [];
    const list = mainCompany.id
      ? [{ id: mainCompany.id, full_name: mainCompany.full_name, short_name: mainCompany.short_name }]
      : [];
    const partners = mainCompany.partner_companies ?? [];
    const all = [...list, ...partners];
    return all
      .filter((c) => c.id != null)
      .map((c) => ({ label: c.full_name || c.short_name || String(c.id), value: String(c.id!) }));
  }, [mainCompany]);

  const updateField = useCallback(
    (field: keyof typeof formState, value: string | string[] | File | null) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const buildPayload = useCallback((): IUserForm => {
    const languageCode =
      formState.language.trim() ||
      mainCompany?.default_language_code?.trim() ||
      undefined;

    const profileAttrs: IUserForm['user']['profile_attributes'] & { photo?: Blob } = {
      first_name: formState.first_name,
      second_name: formState.second_name,
      last_name: formState.last_name,
      id_code: formState.id_code,
      language: languageCode ?? '',
      phone: formState.phone,
      landline_phone: formState.landline_phone
    };
    if (formState.photo) {
      profileAttrs.photo = formState.photo;
    }

    return {
      user: {
        email: formState.email,
        password: formState.password,
        password_confirmation: formState.password_confirmation,
        role: formState.role,
        company_id: formState.company_id || undefined,
        function_ids: formState.role_in_company.map((id) => Number(id)),
        area_ids: formState.area_ids.map((id) => Number(id)),
        profile_attributes: profileAttrs
      }
    } as unknown as IUserForm;
  }, [formState, mainCompany?.default_language_code]);

  const handleSave = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await createUserMutation.mutateAsync(buildPayload());
      setFormState(defaultFormStateForCompany(mainCompany ?? undefined));
      onCancelClick();
    } catch (e) {
      const message = e instanceof Error ? e.message : null;
      toast.error(message && message !== 'anErrorOccurred' ? message : t('common.anErrorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  }, [buildPayload, createUserMutation, onCancelClick, t]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      isSecondButtonVisible
      headerTitle={t('employeesAndUsersView.addUser')}
      mainButton={{
        action: handleSave,
        label: t('buttons.add'),
        isDisabled: isSubmitting
      }}
    >
      <Flex flexDirection="column">
        <Row flexWrap={['wrap', 'nowrap']}>
          <Select
            withoutMargin
            label={t('employeesAndUsersView.form.companyName')}
            options={companyOptions}
            selectedOptions={
              formState.company_id
                ? companyOptions.find((o) => o.value === formState.company_id) ?? undefined
                : undefined
            }
            onChange={(option) =>
              updateField(
                'company_id',
                Array.isArray(option) ? option[0]?.value ?? '' : option?.value ?? ''
              )
            }
          />
          <TextField
            label="Kod ID"
            disabled
            value={formState.id_code}
            onChange={(e) => updateField('id_code', e.target.value)}
          />
          <ImageInput
            type="photo"
            placeholder={t('employeesAndUsersView.form.userPhotoPlaceholder')}
            setValue={(file) =>
              updateField('photo', file ? (file as File) : null)
            }
          />
        </Row>
        <Row flexWrap={['wrap', 'nowrap']}>
          <TextField
            label={t('employeesAndUsersView.form.firstName')}
            value={formState.first_name}
            onChange={(e) => updateField('first_name', e.target.value)}
          />
          <TextField
            label={t('employeesAndUsersView.form.secondName')}
            value={formState.second_name}
            onChange={(e) => updateField('second_name', e.target.value)}
          />
          <TextField
            label={t('employeesAndUsersView.form.lastName')}
            value={formState.last_name}
            onChange={(e) => updateField('last_name', e.target.value)}
          />
        </Row>
        <Row flexWrap={['wrap', 'nowrap']}>
          <TextField
            label={t('employeesAndUsersView.form.emailAddress')}
            value={formState.email}
            onChange={(e) => updateField('email', e.target.value)}
            autoComplete="off"
          />
          <TextField
            label={t('employeesAndUsersView.form.phone')}
            value={formState.phone}
            onChange={(e) => updateField('phone', e.target.value)}
          />
          <TextField
            label={t('employeesAndUsersView.form.mobilePhone')}
            value={formState.landline_phone}
            onChange={(e) => updateField('landline_phone', e.target.value)}
          />
        </Row>
        <Row flexWrap={['wrap', 'nowrap']}>
          <TextField
            name="new-account-password"
            label={t('employeesAndUsersView.form.password')}
            type="password"
            value={formState.password}
            onChange={(e) => updateField('password', e.target.value)}
            autoComplete="new-password"
            autoCorrect="off"
            spellCheck={false}
          />
          <TextField
            name="new-account-password-confirm"
            label={t('employeesAndUsersView.form.passwordRepeat')}
            type="password"
            value={formState.password_confirmation}
            onChange={(e) => updateField('password_confirmation', e.target.value)}
            autoComplete="new-password"
            autoCorrect="off"
            spellCheck={false}
          />
        </Row>
        <Row flexWrap={['wrap', 'nowrap']}>
          <Select
            label={t('employeesAndUsersView.form.language')}
            options={languageOptions}
            selectedOptions={
              formState.language
                ? languageOptions.find((o) => o.value === formState.language) ?? undefined
                : undefined
            }
            onChange={(option) =>
              updateField(
                'language',
                Array.isArray(option) ? option[0]?.value ?? '' : option?.value ?? ''
              )
            }
          />
          <NestedSelect
            label={t('employeesAndUsersView.form.mainArea')}
            options={mainAreaOptions}
            selectedOptions={mainAreaSelectedOptions}
            setSelectedOptions={(opts) =>
              updateField(
                'area_ids',
                collapseSelectedAreaPermissionIds(mainAreaOptions, opts).map((id) =>
                  String(id)
                )
              )
            }
          />
          <Select
            label={t('employeesAndUsersView.form.roleInCompany')}
            options={roleInCompanyOptions}
            isMulti
            selectedOptions={roleInCompanyOptions.filter((o) =>
              formState.role_in_company.includes(o.value)
            )}
            onChange={(option) =>
              updateField(
                'role_in_company',
                Array.isArray(option) ? option.map((o) => o.value) : option ? [option.value] : []
              )
            }
          />
        </Row>
        <Row flexWrap={['wrap', 'nowrap']} flexDirection="column" alignItems="flex-start" alignSelf="flex-start" width="100%">
          <P variant="body" mb={2}>
            {t('employeesAndUsersView.tableHeaders.accountType')}
          </P>
          <RadioGroup alignSelf="flex-start">
            <Radio
              name="userRole"
              value="user"
              label="administration.user"
              checked={formState.role === 'user'}
              onClick={() => updateField('role', 'user')}
            />
            <Radio
              name="userRole"
              value="admin"
              label="administration.admin"
              checked={formState.role === 'admin'}
              onClick={() => updateField('role', 'admin')}
            />
            <Radio
              name="userRole"
              value="consultant"
              label="administration.consultant"
              checked={formState.role === 'consultant'}
              onClick={() => updateField('role', 'consultant')}
            />
            <Radio
              name="userRole"
              value="guest"
              label="administration.guest"
              checked={formState.role === 'guest'}
              onClick={() => updateField('role', 'guest')}
            />
          </RadioGroup>
        </Row>
      </Flex>
    </Modal>
  );
};
